import * as React from 'react'
import FlexView from '@/modules/shared/components/FlexView'
import moment from 'moment'

import { DriverCalendarDayModel } from 'app/models/calendar/driverCalendarDayModel'
import { CalendarMenuAction } from './CalendarDayItem'
import { DayType } from 'app/constants/dayType'
import { isLeaveDayType, isTruancyDayType } from 'app/common/dayTypeHelper'
import { usePermissionChecker } from '@/modules/shared/hooks/usePermissionChecker'
import { useDayEvents } from '../../../../../hooks/useDayEvents'
import {
  faCarSolid,
  faFileCertificateSolid,
  FaIcon,
  faMapPinSolid,
  faPenToSquareSolid,
  faPrintSolid,
  faRotateLeftSolid,
  faTrashSolid,
} from '@/lib/fa'
import useCalendarService from '@/modules/drivers/services/CalendarService'
import { ConfirmModal } from '@/modules/drivers/modals/ConfirmModal'

import {
  MenuItem,
  SubMenu,
  MenuDivider,
  ControlledMenu,
} from '@szhsin/react-menu'
import '@szhsin/react-menu/dist/index.css'
import useFormatDate from '@/modules/drivers/hooks/useFormatDate'
import AddAnotherDayActivityModal from '@/modules/drivers/modals/calendar/AddAnotherDayActivityModal'
import TachoDisc from '@/modules/drivers/tabs/activities/tacho_disc/TachoDisc'
import { createPortal } from 'react-dom'
import { useQueries } from '@tanstack/react-query'
import { getChartDisc } from '@/lib/api/company/chartScan'
type DayContextMenuProps = {
  companyId: string
  day: DriverCalendarDayModel
  driverId: number
  translate: (text) => any
  onCalendarAction: (
    day: DriverCalendarDayModel,
    action: CalendarMenuAction,
    data?: any,
  ) => void
  refreshCalendar?: () => Promise<void>
  anchorPoint?: { x: number; y: number }
  isOpen?: boolean
  onClose?: () => void
  selectedDays?: Array<DriverCalendarDayModel>
  currentSelectedDay?: DriverCalendarDayModel | null
  setSelectedDay?: (day: DriverCalendarDayModel | null) => void
  openCertificateModal: (dates?: Array<Date | string>) => void
}

export const DayContextMenu: React.FC<DayContextMenuProps> = (props) => {
  const {
    day,
    companyId,
    driverId,
    translate,
    refreshCalendar,
    anchorPoint,
    isOpen = false,
    onClose,
    selectedDays = [],
    openCertificateModal,
  } = props
  const { changeDayTypes } = useCalendarService()
  const dayEvents = useDayEvents()
  const [
    isAddAnotherDayActivityModalOpen,
    setIsAddAnotherDayActivityModalOpen,
  ] = React.useState(false)
  const [isConfirmModalOpen, setIsConfirmModalOpen] = React.useState(false)
  const [selectedDayType, setSelectedDayType] = React.useState<DayType | null>(
    null,
  )
  const [isLoading, setIsLoading] = React.useState(false)
  const [editChartId, setEditChartId] = React.useState<number | null>(null)
  const chartIds = day.tachographChartIds ?? []
  const chartDiscs = useQueries({
    queries: chartIds.map((id) => ({
      queryKey: ['chart-scan-disc', companyId, id, false],
      queryFn: () => getChartDisc(companyId, id, false).then((r) => r.data),
      enabled: isOpen && chartIds.length > 1,
    })),
  })
  const chartLabel = (index: number) => {
    const disc = chartDiscs[index]?.data?.discs?.[0]
    if (!disc) return `#${index + 1}`
    const time = (value: string) => moment(value).format('HH:mm')
    return `${time(disc.dayStartDate)} – ${time(disc.dayEndDate)}`
  }
  const [confirmAction, setConfirmAction] = React.useState<{
    title: string
    text: string
    action: () => Promise<void>
    datesList?: string[]
  } | null>(null)

  const can = usePermissionChecker(companyId)

  const formatDateToString = (date: any): string => {
    if (date && typeof date.format === 'function') {
      return date.format('YYYY-MM-DD')
    } else if (typeof date === 'string') {
      return date.split('T')[0]
    } else if (date instanceof Date) {
      return moment(date).format('YYYY-MM-DD')
    }
    console.error('Unknown date format:', date)
    return ''
  }

  const getSelectedDates = (): string[] => {
    if (selectedDays && selectedDays.length > 0) {
      return selectedDays
        .map((d) => formatDateToString(d.activityDayLocalTimeDate))
        .filter((d) => d !== '')
    }
    return [formatDateToString(day.activityDayLocalTimeDate)]
  }

  const { formatDate } = useFormatDate()
  const formatDatesForDisplay = (): string => {
    const dateStrings = getSelectedDates()

    if (dateStrings.length === 1) {
      return formatDate(dateStrings[0])
    }

    const sortedDates = [...dateStrings].sort()

    const ranges: string[][] = []
    let currentRange: string[] = [sortedDates[0]]

    for (let i = 1; i < sortedDates.length; i++) {
      const prevDate = moment(sortedDates[i - 1])
      const currDate = moment(sortedDates[i])
      const diffInDays = currDate.diff(prevDate, 'days')

      if (diffInDays === 1) {
        currentRange.push(sortedDates[i])
      } else {
        ranges.push(currentRange)
        currentRange = [sortedDates[i]]
      }
    }
    ranges.push(currentRange)

    const formattedRanges = ranges.map((range) => {
      if (range.length === 1) {
        return formatDate(range[0])
      } else {
        return `${formatDate(range[0])} - ${formatDate(range[range.length - 1])}`
      }
    })

    if (formattedRanges.length > 5) {
      return `${sortedDates.length} dni (${formatDate(sortedDates[0])} - ${formatDate(sortedDates[sortedDates.length - 1])})`
    }

    return formattedRanges.join(', ')
  }

  const getFormattedDatesList = (): string[] => {
    const dateStrings = getSelectedDates()

    if (dateStrings.length === 1) {
      return []
    }

    const sortedDates = [...dateStrings].sort()

    const ranges: string[][] = []
    let currentRange: string[] = [sortedDates[0]]

    for (let i = 1; i < sortedDates.length; i++) {
      const prevDate = moment(sortedDates[i - 1])
      const currDate = moment(sortedDates[i])
      const diffInDays = currDate.diff(prevDate, 'days')

      if (diffInDays === 1) {
        currentRange.push(sortedDates[i])
      } else {
        ranges.push(currentRange)
        currentRange = [sortedDates[i]]
      }
    }
    ranges.push(currentRange)

    return ranges.map((range) => {
      if (range.length === 1) {
        return formatDate(range[0])
      } else {
        return `${formatDate(range[0])} - ${formatDate(range[range.length - 1])} (${range.length} dni)`
      }
    })
  }

  const closeMenu = () => {
    if (onClose) {
      onClose()
    }
  }

  const handleConfirmDayTypeChange = async () => {
    if (selectedDayType !== null) {
      setIsLoading(true)
      closeMenu()
      try {
        await dayEvents.handleChangeDayType(
          day,
          selectedDayType,
          companyId,
          driverId,
          getSelectedDates(),
          changeDayTypes,
        )
        if (refreshCalendar) {
          await refreshCalendar()
        }
      } catch (error) {
        console.error('Error changing day type:', error)
      } finally {
        setIsLoading(false)
      }
    }
    setIsConfirmModalOpen(false)
    setSelectedDayType(null)
  }

  const handleConfirmAction = async () => {
    if (confirmAction) {
      setIsLoading(true)
      closeMenu()
      try {
        await confirmAction.action()
        if (refreshCalendar) {
          await refreshCalendar()
        }
      } catch (error) {
        console.error('Error executing action:', error)
      } finally {
        setIsLoading(false)
        setConfirmAction(null)
      }
    }
  }

  const createConfirmAction = (
    title: string,
    text: string,
    action: () => Promise<void>,
  ) => {
    setConfirmAction({
      title,
      text:
        getSelectedDates().length === 1
          ? text
          : text.replace(
              /dla:.*\?/,
              `${translate('driverCalendar.for-following-days')}?`,
            ),
      action,
      datesList: getFormattedDatesList(),
    })
  }

  const renderDayTypeMenuItem = (dayType: DayType, disabled?: boolean) => {
    return (
      <MenuItem
        className={`tw-my-0 tw-px-4 tw-py-1 tw-text-sm tw-text-content-base ${disabled ? 'tw-cursor-not-allowed tw-opacity-50' : 'tw-cursor-pointer'}`}
        onClick={(e) => {
          setSelectedDayType(dayType)
          setIsConfirmModalOpen(true)
          closeMenu()
        }}
      >
        <FlexView hAlignContent="left" vAlignContent="center">
          <span style={{ fontWeight: 500, width: 48 }}>{DayType[dayType]}</span>
          <span style={{ marginRight: 5 }}>
            {translate(`dayType.${DayType[dayType]}`)}
          </span>
        </FlexView>
      </MenuItem>
    )
  }

  return (
    <>
      <ControlledMenu
        anchorPoint={anchorPoint}
        state={isOpen ? 'open' : 'closed'}
        onClose={closeMenu}
        className="tw-z-[10000]"
        portal={true}
        onContextMenu={(e) => {
          e.preventDefault()
        }}
      >
        <MenuItemStyled
          icon={faFileCertificateSolid}
          // Kierowca dostaje zaświadczenie o sobie także bez tego uprawnienia (serwer: DriverPermissionValidator).
          disabled={!can('canGenerateAttestation') && !can('isDriver')}
          onClick={(e) => {
            openCertificateModal(getSelectedDates())
            closeMenu()
          }}
        >
          {translate('driverCalendarMenuActions.generateAttestation')}
        </MenuItemStyled>

        <MenuItemStyled
          disabled={
            (day.dayType != DayType.Bw &&
              day.dayType != DayType.Uz &&
              !(
                isLeaveDayType(day.dayType) && !isTruancyDayType(day.dayType)
              )) ||
            !can('canEditActivityDay')
          }
          icon={faPrintSolid}
          onClick={(e) => {
            dayEvents.handlePrintApplications(day)
            closeMenu()
          }}
        >
          {translate('driverCalendarMenuActions.printApplications')}
        </MenuItemStyled>

        <MenuDivider className={'tw-m-0 tw-p-0'} />

        <MenuItemStyled
          disabled={!can('canEditActivities')}
          icon={faPenToSquareSolid}
          onClick={(e) => {
            setIsAddAnotherDayActivityModalOpen(true)
          }}
        >
          {translate('driverCalendarMenuActions.insertActivityFromAnotherDay')}
        </MenuItemStyled>

        {chartIds.length > 1 ? (
          <SubItemStyled
            disabled={!can('canEditActivities')}
            label={translate('driverCalendarMenuActions.editChart')}
          >
            {chartIds.map((id, i) => (
              <MenuItemStyled
                key={id}
                onClick={() => {
                  setEditChartId(id)
                  closeMenu()
                }}
              >
                {chartLabel(i)}
              </MenuItemStyled>
            ))}
          </SubItemStyled>
        ) : (
          <MenuItemStyled
            disabled={chartIds.length === 0 || !can('canEditActivities')}
            icon={faPenToSquareSolid}
            onClick={() => {
              setEditChartId(chartIds[0])
              closeMenu()
            }}
          >
            {translate('driverCalendarMenuActions.editChart')}
          </MenuItemStyled>
        )}

        <MenuItemStyled
          disabled={!can('canEditActivities')}
          icon={faTrashSolid}
          className="tw-text-red-500"
          onClick={(e) => {
            createConfirmAction(
              translate('driverCalendarMenuActions.removeDigitalData'),
              `${translate('driverCalendar.confirm-remove-digital-data')} ${formatDatesForDisplay()}?`,
              () =>
                dayEvents.handleRemoveDigitalData(
                  companyId,
                  driverId,
                  getSelectedDates(),
                  'driver',
                ),
            )
          }}
        >
          {translate('driverCalendarMenuActions.removeDigitalData')}
        </MenuItemStyled>

        <MenuDivider className={'tw-m-0 tw-p-0'} />

        <MenuItemStyled
          disabled={!can('canEditGpsData')}
          icon={faMapPinSolid}
          onClick={(e) => {
            createConfirmAction(
              translate(
                'driverCalendarMenuActions.assignBorderCrossingsFromVehicle',
              ),
              `${translate('driverCalendar.confirm-assign-border-crossings')} ${formatDatesForDisplay()}?`,
              () =>
                dayEvents.handleAssignBorderCrossingsFromVehicle(
                  companyId,
                  driverId,
                  getSelectedDates(),
                  'driver',
                ),
            )
          }}
        >
          {translate(
            'driverCalendarMenuActions.assignBorderCrossingsFromVehicle',
          )}
        </MenuItemStyled>

        <MenuItemStyled
          disabled={!day.hasGpsData || !can('canEditGpsData')}
          icon={faTrashSolid}
          className="tw-text-red-500"
          onClick={(e) => {
            createConfirmAction(
              translate('driverCalendarMenuActions.removeBorderCrossings'),
              `${translate('driverCalendar.confirm-remove-border-crossings')} ${formatDatesForDisplay()}?`,
              () =>
                dayEvents.handleRemoveBorderCrossings(
                  companyId,
                  driverId,
                  getSelectedDates(),
                  'driver',
                ),
            )
          }}
        >
          {translate('driverCalendarMenuActions.removeBorderCrossings')}
        </MenuItemStyled>

        <MenuDivider className={'tw-m-0 tw-p-0'} />

        <MenuItemStyled
          disabled={!can('canEditGpsData')}
          icon={faCarSolid}
          onClick={(e) => {
            createConfirmAction(
              translate(
                'driverCalendarMenuActions.assignTmsDataToDriverAutomatically',
              ),
              `${translate('driverCalendar.confirm-assign-tms-data')} ${formatDatesForDisplay()}?`,
              () =>
                dayEvents.handleAssignTmsDataToDriverAutomatically(
                  companyId,
                  driverId,
                  getSelectedDates(),
                  'driver',
                ),
            )
          }}
        >
          {translate(
            'driverCalendarMenuActions.assignTmsDataToDriverAutomatically',
          )}
        </MenuItemStyled>

        <MenuItemStyled
          disabled={!day.hasTmsData || !can('canEditGpsData')}
          icon={faTrashSolid}
          className="tw-text-red-500"
          onClick={(e) => {
            createConfirmAction(
              translate('driverCalendarMenuActions.removeTmsData'),
              `${translate('driverCalendar.confirm-remove-tms-data')} ${formatDatesForDisplay()}?`,
              () =>
                dayEvents.handleRemoveTmsData(
                  companyId,
                  driverId,
                  getSelectedDates(),
                  'driver',
                ),
            )
          }}
        >
          {translate('driverCalendarMenuActions.removeTmsData')}
        </MenuItemStyled>

        <MenuDivider className={'tw-m-0 tw-p-0'} />

        <MenuItemStyled
          disabled={!can('canEditActivities')}
          icon={faRotateLeftSolid}
          className="tw-text-primary"
          onClick={(e) => {
            createConfirmAction(
              translate('driverCalendarMenuActions.restoreOriginalCardData'),
              `${translate('driverCalendar.confirm-restore-card-data')} ${formatDatesForDisplay()}?`,
              () =>
                dayEvents.handleRestoreOriginalCardData(
                  companyId,
                  driverId,
                  getSelectedDates(),
                  'driver',
                ),
            )
          }}
        >
          {translate('driverCalendarMenuActions.restoreOriginalCardData')}
        </MenuItemStyled>

        {/* <MenuItemStyled
          disabled={true}
          onClick={(e) => {
            dayEvents.handleShowDetails(day)
            closeMenu()
          }}
        >
          {translate('driverCalendarMenuActions.showDetails')}
        </MenuItemStyled> */}

        {/* <MenuItemStyled
          disabled={!can('canEditActivityDay')}
          onClick={(e) => {
            createConfirmAction(
              translate('driverCalendarMenuActions.restoreDefaultDayType'),
              `${translate('driverCalendar.confirm-restore-default-day-type')} ${formatDatesForDisplay()}?`,
              () =>
                dayEvents.handleRestoreDefaultDayType(
                  companyId,
                  driverId,
                  getSelectedDates(),
                  changeDayTypes,
                ),
            )
          }}
        >
          {translate('driverCalendarMenuActions.restoreDefaultDayType')}
        </MenuItemStyled> */}
        <MenuItemStyled
          disabled={
            (day.dayType != DayType.Bw &&
              day.dayType != DayType.Uz &&
              !(
                isLeaveDayType(day.dayType) && !isTruancyDayType(day.dayType)
              )) ||
            !can('canEditActivityDay')
          }
          onClick={(e) => {
            dayEvents.handlePrintApplications(day)
            closeMenu()
          }}
        >
          {translate('driverCalendarMenuActions.printApplications')}
        </MenuItemStyled>

        <MenuDivider className={'tw-m-0 tw-p-0'} />

        <SubItemStyled
          disabled={!can('canEditActivityDay')}
          label={translate('driverCalendarMenuActions.insertDayType.title')}
        >
          <SubItemStyled
            label={translate(
              'driverCalendarMenuActions.insertDayType.freeTime',
            )}
          >
            {renderDayTypeMenuItem(DayType.Wo)}
            <SubItemStyled
              label={translate(
                'driverCalendarMenuActions.insertDayType.WoOptions',
              )}
            >
              {renderDayTypeMenuItem(DayType.WoC)}
              {renderDayTypeMenuItem(DayType.WoCn)}
              {renderDayTypeMenuItem(DayType.WoCb)}
              {renderDayTypeMenuItem(DayType.WoOo)}
              {renderDayTypeMenuItem(DayType.WoOd)}
              {renderDayTypeMenuItem(DayType.WoOs)}
              {renderDayTypeMenuItem(DayType.WoOr)}
              {renderDayTypeMenuItem(DayType.WoRb)}
              {renderDayTypeMenuItem(DayType.WoRc)}
              {renderDayTypeMenuItem(DayType.WoRw)}
              {renderDayTypeMenuItem(DayType.WoZ)}
              {renderDayTypeMenuItem(DayType.WoZb)}
              {renderDayTypeMenuItem(DayType.WoZn)}
              {renderDayTypeMenuItem(DayType.WoZs)}
              {renderDayTypeMenuItem(DayType.WoZw)}
              {renderDayTypeMenuItem(DayType.WoUoj)}
              {renderDayTypeMenuItem(DayType.WoM)}
            </SubItemStyled>
          </SubItemStyled>
          {renderDayTypeMenuItem(DayType.U)}
          {renderDayTypeMenuItem(DayType.C)}
          {renderDayTypeMenuItem(DayType.Uo)}
          {renderDayTypeMenuItem(DayType.Nn)}
          {renderDayTypeMenuItem(DayType.Bw)}
          {renderDayTypeMenuItem(DayType.M)}
          {renderDayTypeMenuItem(DayType.Uoj)}
          <MenuDivider className={'tw-m-0 tw-p-0'} />
          <SubItemStyled
            label={translate('driverCalendar.day-type-change.leaves')}
          >
            {renderDayTypeMenuItem(DayType.Uz)}
            {renderDayTypeMenuItem(DayType.Ui)}
            {renderDayTypeMenuItem(DayType.Un)}
            {renderDayTypeMenuItem(DayType.Ur)}
            {renderDayTypeMenuItem(DayType.Ux)}
            {renderDayTypeMenuItem(DayType.Us)}
            {renderDayTypeMenuItem(DayType.Um)}
            {renderDayTypeMenuItem(DayType.Uf)}
            {renderDayTypeMenuItem(DayType.Bp)}
            {renderDayTypeMenuItem(DayType.D1)}
            {renderDayTypeMenuItem(DayType.D2)}
          </SubItemStyled>
          <SubItemStyled
            label={translate('driverCalendar.day-type-change.grants')}
          >
            {renderDayTypeMenuItem(DayType.Z)}
            {renderDayTypeMenuItem(DayType.Zb)}
            {renderDayTypeMenuItem(DayType.Zs)}
            {renderDayTypeMenuItem(DayType.Zw)}
            {renderDayTypeMenuItem(DayType.Zn)}
            {renderDayTypeMenuItem(DayType.Od)}
            {renderDayTypeMenuItem(DayType.Os)}
            {renderDayTypeMenuItem(DayType.Or)}
            {renderDayTypeMenuItem(DayType.Oo)}
            {renderDayTypeMenuItem(DayType.Cn)}
            {renderDayTypeMenuItem(DayType.Cb)}
          </SubItemStyled>
          <SubItemStyled
            label={translate('driverCalendar.day-type-change.benefits')}
          >
            {renderDayTypeMenuItem(DayType.Rc)}
            {renderDayTypeMenuItem(DayType.Rw)}
            {renderDayTypeMenuItem(DayType.Rb)}
          </SubItemStyled>
          <SubItemStyled
            label={translate('driverCalendar.day-type-change.others')}
          >
            {renderDayTypeMenuItem(DayType.Ps)}
            {renderDayTypeMenuItem(DayType.Pw)}
            {renderDayTypeMenuItem(DayType.U2)}
            {renderDayTypeMenuItem(DayType.Np)}
            {renderDayTypeMenuItem(DayType.Nu)}
            {renderDayTypeMenuItem(DayType.Sw)}
            {renderDayTypeMenuItem(DayType.Prz)}
          </SubItemStyled>
        </SubItemStyled>

        <MenuItemStyled
          disabled={!can('canEditActivityDay')}
          onClick={(e) => {
            createConfirmAction(
              translate('driverCalendarMenuActions.restoreDefaultDayType'),
              `${translate('driverCalendar.confirm-restore-default-day-type')} ${formatDatesForDisplay()}?`,
              () =>
                dayEvents.handleRestoreDefaultDayType(
                  companyId,
                  driverId,
                  getSelectedDates(),
                  changeDayTypes,
                ),
            )
          }}
        >
          {translate('driverCalendarMenuActions.restoreDefaultDayType')}
        </MenuItemStyled>
      </ControlledMenu>

      <ConfirmModal
        isOpen={isConfirmModalOpen}
        onClose={() => {
          setIsConfirmModalOpen(false)
          setSelectedDayType(null)
        }}
        title={translate('driverCalendar.change-day-type-title')}
        text={`${translate('driverCalendar.confirm-change-day-type')} ${selectedDayType !== null ? translate(`dayType.${DayType[selectedDayType]}`) : ''}${getSelectedDates().length === 1 ? ` ${translate('driverCalendar.for')}: ${formatDatesForDisplay()}` : ` ${translate('driverCalendar.for-following-days')}:`}?`}
        actionText={translate('driverCalendar.change')}
        onConfirm={handleConfirmDayTypeChange}
        content={getFormattedDatesList()}
      />

      {confirmAction && (
        <ConfirmModal
          isOpen={true}
          onClose={() => setConfirmAction(null)}
          title={confirmAction.title}
          text={confirmAction.text}
          actionText={translate('common.confirm')}
          onConfirm={handleConfirmAction}
          content={confirmAction.datesList || []}
        />
      )}
      {editChartId != null &&
        createPortal(
          <TachoDisc
            companyId={companyId}
            discId={editChartId}
            onClose={() => setEditChartId(null)}
            onSaved={() => refreshCalendar?.()}
          />,
          document.body,
        )}
      {isAddAnotherDayActivityModalOpen && (
        <AddAnotherDayActivityModal
          driverId={driverId}
          close={() => setIsAddAnotherDayActivityModalOpen(false)}
          selectedDay={selectedDays[0]}
          companyId={companyId}
        />
      )}
      {isLoading && (
        <div className="tw-fixed tw-inset-0 tw-z-[9999] tw-flex tw-items-center tw-justify-center tw-bg-black tw-bg-opacity-50">
          <div className="tw-rounded-lg tw-bg-white tw-p-8 tw-shadow-xl">
            <div className="tw-flex tw-flex-col tw-items-center tw-gap-4">
              <div className="tw-h-12 tw-w-12 tw-animate-spin tw-rounded-full tw-border-4 tw-border-gray-200 tw-border-t-primary"></div>
              <p className="tw-text-lg tw-font-medium tw-text-gray-700">
                {translate('driverCalendar.updating-calendar')}
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

const MenuItemStyled = ({
  disabled,
  children,
  onClick,
  icon,
  className: extraClassName,
}: {
  children: React.ReactNode
  disabled?: boolean
  onClick?: (e: any) => void
  icon?: any
  className?: string
}) => {
  return (
    <MenuItem
      disabled={disabled}
      className={`tw-my-0 tw-px-4 tw-py-1 tw-text-sm tw-text-content-base ${disabled ? 'tw-cursor-not-allowed tw-opacity-50' : 'tw-cursor-pointer'}`}
      onClick={onClick}
    >
      <FlexView
        hAlignContent="left"
        vAlignContent="center"
        className="tw-gap-2"
      >
        {icon && (
          <FaIcon
            icon={icon}
            className={`tw-h-3 tw-w-3 ${extraClassName || ''}`}
          />
        )}
        <span className={extraClassName || ''}>{children}</span>
      </FlexView>
    </MenuItem>
  )
}

const SubItemStyled = ({
  disabled,
  children,
  label,
}: {
  children: React.ReactNode
  disabled?: boolean
  label: string
}) => {
  return (
    <SubMenu disabled={disabled} label={label}>
      {children}
    </SubMenu>
  )
}
